import { AxiosResponse } from "axios";

import apiClient from "../../../../shared/infrastructure/api/client";
import { uploadMeditationAudio } from "./Meditations.api";
import {
    MeditationLikeState,
    MeditationPlayback,
    MeditationTrack,
    MeditationTrackFilters,
    MeditationTrackUpload,
} from "../../domain/types";

export const MeditationTracksAPI = {
    list: (): Promise<AxiosResponse<MeditationTrack[]>> =>
        apiClient.get("/meditation-tracks"),

    /** The shelf of the caller's centre. Empty for a therapist in none. */
    listCenter: (): Promise<AxiosResponse<MeditationTrack[]>> =>
        apiClient.get("/meditation-tracks/center"),

    /** The public catalogue of music, the caller's own shared tracks included. */
    listPublic: (
        filters: MeditationTrackFilters = {},
        page: { offset?: number; limit?: number } = {}
    ): Promise<AxiosResponse<MeditationTrack[]>> =>
        apiClient.get("/meditation-tracks/public", {
            params: {
                ...(filters.search ? { search: filters.search } : {}),
                ...(filters.author ? { author: filters.author } : {}),
                ...(filters.order ? { order: filters.order } : {}),
                ...page,
            },
        }),

    /** Put someone else's shared track on the caller's own shelf. */
    copy: (id: string): Promise<AxiosResponse<MeditationTrack>> =>
        apiClient.post(`/meditation-tracks/${id}/copy`),

    like: (id: string): Promise<AxiosResponse<MeditationLikeState>> =>
        apiClient.post(`/meditation-tracks/${id}/like`),

    unlike: (id: string): Promise<AxiosResponse<MeditationLikeState>> =>
        apiClient.delete(`/meditation-tracks/${id}/like`),

    /**
     * Share one of the caller's own tracks with everybody, or stop sharing it.
     * Sharing needs the caller's word that the music is theirs to share.
     */
    setVisibility: (
        id: string,
        visibility: { public: boolean; rights_confirmed?: boolean; author_name?: string }
    ): Promise<AxiosResponse<MeditationTrack>> =>
        apiClient.put(`/meditation-tracks/${id}/visibility`, visibility),

    uploadUrl: (
        contentType: string
    ): Promise<AxiosResponse<MeditationTrackUpload>> =>
        apiClient.post("/meditation-tracks/upload-url", {
            content_type: contentType,
        }),

    create: (track: {
        name: string;
        audio_path: string;
        duration_seconds?: number | null;
        rights_confirmed: boolean;
        /** Onto the centre's shelf. Administrators only. */
        for_center?: boolean;
    }): Promise<AxiosResponse<MeditationTrack>> =>
        apiClient.post("/meditation-tracks", track),

    /** Fill in a length the track was uploaded without. The owner's only; a known length is kept. */
    recordDuration: (id: string, seconds: number): Promise<AxiosResponse<MeditationTrack>> =>
        apiClient.put(`/meditation-tracks/${id}/duration`, { duration_seconds: seconds }),

    rename: (id: string, name: string): Promise<AxiosResponse<MeditationTrack>> =>
        apiClient.patch(`/meditation-tracks/${id}`, { name }),

    remove: (id: string): Promise<AxiosResponse<void>> =>
        apiClient.delete(`/meditation-tracks/${id}`),

    playback: (id: string): Promise<AxiosResponse<MeditationPlayback>> =>
        apiClient.get(`/meditation-tracks/${id}/playback`),

    /** The track's bytes, through our own API rather than from the bucket. */
    audio: (id: string): Promise<AxiosResponse<Blob>> =>
        apiClient.get(`/meditation-tracks/${id}/audio`, { responseType: "blob" }),
};

export { uploadMeditationAudio };
